// Adding domains to a list: typed (one or many, commas or lines), checked with the server's
// rules first; or a `.txt`/`.csv` uploaded and parsed by the API, which says what it added.
import { Plus, UploadSimple } from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { Button, Input } from "rizzui";

import { useT } from "../i18n";
import { toast } from "../layout/toast";
import { changeDomains, importDomains } from "./api";
import { parseDomains } from "./domains";
import type { ListChange } from "./useListChange";

export function AddDomains({ listId, change }: { listId: string; change: ListChange }) {
  const t = useT().network.list;
  const tc = useT().network.create;
  const [text, setText] = useState("");
  const file = useRef<HTMLInputElement>(null);
  const parsed = parseDomains(text);

  async function add() {
    const done = await change(() => changeDomains(listId, { add: parsed.valid }), () => t.added(parsed.valid.length));
    if (done) setText("");
  }

  async function upload(f: File) {
    const body = await f.text();
    const result = await change(() => importDomains(listId, body), (r) => t.uploaded(r.added, r.duplicates));
    if (result && result.invalid.length > 0) toast(t.uploadInvalid(result.invalid.length, result.invalid.slice(0, 3).join(", ")), "warn");
    if (file.current) file.current.value = "";
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <Input
          className="flex-1"
          label={t.addLabel}
          placeholder={t.addPlaceholder}
          value={text}
          inputClassName="font-mono text-[13px]"
          error={parsed.invalid.length > 0 ? tc.invalid(parsed.invalid.slice(0, 5).join(", ")) : undefined}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && parsed.valid.length > 0 && parsed.invalid.length === 0) void add();
          }}
        />
        <div className="flex gap-2">
          <Button className="gap-1.5" disabled={parsed.valid.length === 0 || parsed.invalid.length > 0} onClick={() => void add()}>
            <Plus weight="bold" className="h-4 w-4" /> {t.add}
          </Button>
          <Button variant="outline" className="gap-1.5" onClick={() => file.current?.click()}>
            <UploadSimple weight="bold" className="h-4 w-4" /> {t.upload}
          </Button>
          <input
            ref={file}
            type="file"
            accept=".txt,.csv,text/plain,text/csv"
            className="hidden"
            aria-label={t.upload}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void upload(f);
            }}
          />
        </div>
      </div>
      <p className="m-0 text-[12.5px] text-gray-500">{t.uploadHint}</p>
    </div>
  );
}
